// src/pages/StatePage.js
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import HumanRightsCard from "../../../components/HumanRightsCard";
import DistrictCard from "../../../components/DistrictCard";
import BlockCard from "../../../components/BlockCard";
import districtData from "../../../assets/districts.json";
import { Container, Row, Col, Placeholder } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { apiGet } from "../../../Services";
import DesignationMemberCard from "../../../components/DesignationMemberCard/DesignationMemberCard";
import "./StateCards.css";

const designationPriority = {
  Chairman: 1,
  President: 2,
  "Incharge": 3,
  "Vice President": 4,
  "Working President": 5,
  "General Secretary": 6,
  "Organization Secretary": 7,
  "Joint Secretary": 8,
  "Legal Advisor": 9,
  Treasurer: 10,
  "Media Secretary": 11,
  Spokesman: 12,
};

const sortByDesignation = (members) => {
  return [...members].sort((a, b) => {
    const rankA = designationPriority[a.Designation?.name] || 999;
    const rankB = designationPriority[b.Designation?.name] || 999;
    return rankA - rankB;
  });
};

const StatePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const stateParam = searchParams.get("State")?.replace(/-/g, " ");
  const districtParam = searchParams.get("dist");
  const blockParam = searchParams.get("block");
  const distRef = useRef(null);
  const blockRef = useRef(null);
  const [districts, setDistricts] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [stateMembers, setStateMembers] = useState([]);
  const [districtMembers, setDistrictMembers] = useState([]);
  const [blockMembers, setBlockMembers] = useState([]);
  const [loadingStateMembers, setLoadingStateMembers] = useState(false);
  const [loadingDistrictMembers, setLoadingDistrictMembers] = useState(false);
  const [loadingBlockMembers, setLoadingBlockMembers] = useState(false);

  useEffect(() => {
    if (!stateParam) return;

    const filteredDistricts = districtData.districts.filter(
      (d) => d.state === stateParam
    );
    setDistricts(filteredDistricts);

    if (districtParam) {
      loadBlocks(districtParam);
    }
  }, [stateParam, districtParam]);

  const loadBlocks = async (districtName) => {
    try {
      const blocksFile = await import(
        `../../../assets/BlocksList/${stateParam.replace(/\s/g, "")}.json`
      );
      const districtBlocks = blocksFile[districtName] || [];
      setBlocks(districtBlocks);
    } catch (err) {
      console.error("Blocks not found:", err);
      setBlocks([]);
    }
  };

  const handleDistrictClick = (districtName) => {
    setSearchParams({
      State: stateParam.replace(/\s+/g, "-"),
      dist: districtName,
    });
    loadBlocks(districtName);
  };

  const handleBlockClick = (blockName) => {
    setSearchParams({
      State: stateParam.replace(/\s+/g, "-"),
      dist: districtParam,
      block: blockName,
    });
  };

  // Scroll to district when districtParam changes
  useEffect(() => {
    if (districtParam && distRef.current) {
      distRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [districtParam]);

  // Scroll to block when blockParam changes
  useEffect(() => {
    if (blockParam && blockRef.current) {
      blockRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [blockParam]);

  // Load state members
  useEffect(() => {
    if (stateParam) {
      setLoadingStateMembers(true);
      apiGet(
        `/api/designation-assignments/public?scope=State&state=${stateParam}`
      )
        .then((res) => {
          setStateMembers(res.data);
          setLoadingStateMembers(false);
        })
        .catch((err) => {
          console.log(err);
          setLoadingStateMembers(false);
        });
    }
  }, [stateParam]);

  // Load district members when district is selected
  useEffect(() => {
    if (districtParam && stateParam) {
      setLoadingDistrictMembers(true);
      apiGet(
        `/api/designation-assignments/public?scope=District&state=${stateParam}&district=${districtParam}`
      )
        .then((res) => {
          setDistrictMembers(res.data);
          setLoadingDistrictMembers(false);
        })
        .catch((err) => {
          console.log(err);
          setLoadingDistrictMembers(false);
        });
    }
  }, [districtParam, stateParam]);

  // Load block members when block is selected
  useEffect(() => {
    if (blockParam && districtParam && stateParam) {
      setLoadingBlockMembers(true);
      apiGet(
        `/api/designation-assignments/public?scope=Block&state=${stateParam}&district=${districtParam}&block=${blockParam}`
      )
        .then((res) => {
          setBlockMembers(res.data);
          setLoadingBlockMembers(false);
        })
        .catch((err) => {
          console.log(err);
          setLoadingBlockMembers(false);
        });
    }
  }, [blockParam, districtParam, stateParam]);

  return (
    <>
      <Helmet>
        <title>{stateParam} Governing Body | Manav Adhikar Forum</title>
        <meta
          name="description"
          content={`Explore the ${stateParam} Governing Body of the Manav Adhikar Forum.`}
        />
        <meta
          name="keywords"
          content={`Manav Adhikar Forum, ${stateParam} Governing Body, human rights, equality, justice, India, MAF, awareness, campaigns`}
        />
        <link
          rel="canonical"
          href={`https://manavadhikarforum.in/States?State=${stateParam.replace(
            /\s+/g,
            "-",
          )}`}
        />
      </Helmet>

      <Container className="my-4">
        <HumanRightsCard name={stateParam} level="STATE" />

        <div className="members-section border rounded mb-4 bg-light">
          <h4 className="mb-3 text-center">State Leadership</h4>
          {loadingStateMembers ? (
            <Row className="g-3 justify-content-center">
              {[...Array(4)].map((_, i) => (
                <Col xs={12} sm={10} md={6} lg={4} xl={3} key={i}>
                  <PlaceholderCard />
                </Col>
              ))}
            </Row>
          ) : stateMembers.length > 0 ? (
            <Row className="g-3 justify-content-center">
              {/* {stateMembers.map((m) => (
                <Col xs={12} sm={10} md={6} lg={4} xl={3} key={m.id}>
                  <DesignationMemberCard
                    designation={m.Designation}
                    member={m.Member}
                    level={m.level}
                  />
                </Col>
              ))} */}
              {sortByDesignation(stateMembers).map((m) => (
                <Col xs={12} sm={10} md={6} lg={4} xl={3} key={m.id}>
                  <DesignationMemberCard
                    designation={m.Designation}
                    member={m.Member}
                    level={m.level}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center py-4">
              <div className="empty-state-icon mb-3">
                <i className="bi bi-people display-4 text-muted"></i>
              </div>
              <p className="text-muted">No leadership positions assigned yet</p>
            </div>
          )}
        </div>

        <h4 className="my-3">Districts</h4>
        <Row style={{ rowGap: "0.7rem" }}>
          {districts.map((d) => (
            <Col xs={12} sm={6} md={4} key={d.district}>
              <DistrictCard
                districtName={d.district}
                onClick={handleDistrictClick}
              />
            </Col>
          ))}
        </Row>

        {districtParam && (
          <div ref={distRef} className="mt-4">
            <div className="members-section border rounded mb-4 bg-light">
              <HumanRightsCard name={districtParam} level="DISTRICT" />

              {loadingDistrictMembers ? (
                <Row className="g-3 justify-content-center mt-3">
                  {[...Array(4)].map((_, i) => (
                    <Col xs={12} sm={10} md={6} lg={4} xl={3} key={i}>
                      <PlaceholderCard />
                    </Col>
                  ))}
                </Row>
              ) : districtMembers.length > 0 ? (
                <Row className="g-3 justify-content-center mt-3">
                  {/* {districtMembers.map((m) => (
                    <Col xs={12} sm={10} md={6} lg={4} xl={3} key={m.id}>
                      <DesignationMemberCard
                        designation={m.Designation}
                        member={m.Member}
                        level={m.level}
                      />
                    </Col>
                  ))} */}
                  {sortByDesignation(districtMembers).map((m) => (
                    <Col xs={12} sm={10} md={6} lg={4} xl={3} key={m.id}>
                      <DesignationMemberCard
                        designation={m.Designation}
                        member={m.Member}
                        level={m.level}
                      />
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-center py-4">
                  <div className="empty-state-icon mb-3">
                    <i className="bi bi-people display-4 text-muted"></i>
                  </div>
                  <p className="text-muted">
                    No district leadership assigned yet
                  </p>
                </div>
              )}
            </div>

            <h5 className="my-3">Blocks in {districtParam}</h5>
            <Row>
              {blocks.length ? (
                blocks.map((block) => (
                  <Col xs={12} sm={6} md={4} key={block}>
                    <BlockCard blockName={block} onClick={handleBlockClick} />
                  </Col>
                ))
              ) : (
                <p className="text-muted">
                  No blocks available for this district.
                </p>
              )}
            </Row>
          </div>
        )}

        {blockParam && (
          <div ref={blockRef} className="mt-4">
            <div className="members-section border rounded  bg-light">
              <HumanRightsCard name={blockParam} level="BLOCK" />

              {loadingBlockMembers ? (
                <Row className="g-3 justify-content-center mt-3">
                  {[...Array(4)].map((_, i) => (
                    <Col xs={12} sm={10} md={6} lg={4} xl={3} key={i}>
                      <PlaceholderCard />
                    </Col>
                  ))}
                </Row>
              ) : blockMembers.length > 0 ? (
                <Row className="g-3 justify-content-center mt-3">
                  {/* {blockMembers.map((m) => (
                    <Col xs={12} sm={10} md={6} lg={4} xl={3} key={m.id}>
                      <DesignationMemberCard
                        designation={m.Designation}
                        member={m.Member}
                        level={m.level}
                      />
                    </Col>
                  ))} */}
                  {sortByDesignation(blockMembers).map((m) => (
                    <Col xs={12} sm={10} md={6} lg={4} xl={3} key={m.id}>
                      <DesignationMemberCard
                        designation={m.Designation}
                        member={m.Member}
                        level={m.level}
                      />
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-center py-4">
                  <div className="empty-state-icon mb-3">
                    <i className="bi bi-people display-4 text-muted"></i>
                  </div>
                  <p className="text-muted">No block leadership assigned yet</p>
                </div>
              )}
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

// Placeholder component for loading state
const PlaceholderCard = () => {
  return (
    <div className="card h-100 shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex">
          <Placeholder animation="glow" className="me-3">
            <Placeholder
              xs={8}
              style={{ width: "100px", height: "100px", borderRadius: "8px" }}
            />
          </Placeholder>
          <div className="w-100">
            <Placeholder animation="glow">
              <Placeholder xs={6} /> <Placeholder xs={3} />
            </Placeholder>
            <Placeholder animation="glow" className="mt-2">
              <Placeholder xs={7} />
              <Placeholder xs={4} /> <Placeholder xs={4} className="me-2" />
              <Placeholder xs={6} />
            </Placeholder>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatePage;
